import { AnalyticsService, IDrawerComponent, ITreeNodeItem, ModalRef, TreeviewComponent } from '@activia/ngx-components';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { DashboardFacade } from '../store/dashboard.facade';
import { IDashboardWidget } from '../store/dashboard-widget.interface';
import { DashboardWidgetEnum } from './../dashboard-widgets';

@Component({
  selector: 'amp-dashboard-widgets-drawer',
  templateUrl: './dashboard-widgets-drawer.component.html',
  styleUrls: ['./dashboard-widgets-drawer.component.scss'],
})
export class DashboardWidgetsDrawerComponent implements IDrawerComponent, OnInit, OnDestroy {
  /**
   * Emitted when the data is saved
   */
  @Output() actioned: EventEmitter<any> = new EventEmitter();

  /**
   * Reference to the treeview
   *
   * @ignore
   */
  @ViewChild(TreeviewComponent, { static: false }) treeview: TreeviewComponent<IDashboardWidget>;

  /**
   * The datasource of the treeview
   *
   * @ignore
   */
  datasource$: Observable<ITreeNodeItem<IDashboardWidget>[]>;

  /**
   * The ids of the selected widgets
   *
   * @ignore
   */
  selectedWidgets$: Observable<string[]>;

  /**
   * Specifies if the dashboard is pending
   *
   * @ignore
   */
  pending$ = this.dashboardFacade.pending$;

  /**
   * The list of authorized widgets for the user
   *
   * @ignore
   */
  private authorizedWidgets$: Observable<DashboardWidgetEnum[]>;

  /**
   * Specifies if the user is allowed to save changes made
   *
   * @ignore
   */
  canSave = false;

  /**
   * Pattern used to close all subscriptions
   *
   * @ignore
   */
  private componentDestroyed$: Subject<void> = new Subject();

  /**
   * used to send only modified widgets to analytics
   *
   * @ignore
   */
  private initialSelectionState: { [key: string]: boolean } = {};

  /**
   * @ignore
   */
  constructor(private drawerRef: ModalRef<DashboardWidgetsDrawerComponent>, private dashboardFacade: DashboardFacade, private translate: TranslocoService, private analyticsService: AnalyticsService) {
    // listen for update success action so we can close the drawer and emit the saved event
    // also send the analytics
    // scroll to top of datatable on successful data fetch, if necessary
    this.dashboardFacade.onDashboardWidgetSettingsListUpdateSuccess$.pipe(takeUntil(this.componentDestroyed$)).subscribe((action) => {
      this.sendAnalytics(action.widgetsSettings);
      this.actioned.emit('save');
      this.drawerRef.close();
    });
  }

  ngOnInit() {
    this.authorizedWidgets$ = this.dashboardFacade.authorizedWidgets$;
    this.selectedWidgets$ = this.dashboardFacade.selectedWidgets$;

    this.datasource$ = this.authorizedWidgets$.pipe(
      map((widgets) =>
        widgets.map((widget) => ({
          id: widget,
          label: this.translate.translate('dashboardScope.dashboard.widget.' + widget),
        }))
      )
    );

    // initial selection state
    // also set our initial selection state
    combineLatest([this.authorizedWidgets$, this.selectedWidgets$])
      .pipe(take(1))
      .subscribe(([authorizedWidgets, selectedWidgets]) => {
        authorizedWidgets.forEach((widget) => {
          this.initialSelectionState[widget] = selectedWidgets.includes(widget);
        });
      });
  }

  /**
   * Gets the datasource for the treeview
   *
   * @ignore
   */
  onSave(): void {
    // get the list of selected widgets
    const selectedWidgets = this.treeview.selectedNodes.map((n) => n.id);

    // create a settings entry for each unselected widget
    this.authorizedWidgets$.pipe(take(1)).subscribe((authorizedWidgets) => {
      const widgetsSettings = authorizedWidgets
        .filter((widget) => !selectedWidgets.includes(widget))
        .map((widget) => ({
            name: widget.toString(),
            settings: {
              visible: false,
            },
          }));
      this.dashboardFacade.updateWidgetListSettings(widgetsSettings);
    });
  }

  /**
   * Called when the cancel button is clicked
   *
   * @ignore
   */
  onClose(): void {
    this.drawerRef.close();
  }

  /**
   * Invoked when clicking the overlay.
   *
   * @ignore
   */
  canClose(): boolean {
    return !this.canSave;
  }

  /**
   * @ignore
   */
  public ngOnDestroy(): void {
    // unsubscribe all subscriptions
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  /**
   * Send analytics about the widgets being added/removed
   * // NOTE: this code should be located in the ngrx effects (success action)
   *
   * @ignore
   */
  private sendAnalytics(widgets: IDashboardWidget[]) {
    // the widgets returned from the store are the closed ones
    const closedWidgets = widgets.map((w) => w.name);

    this.authorizedWidgets$.pipe(take(1)).subscribe((authorizedWidgets) =>
      authorizedWidgets.forEach((authorizedWidget) => {
        // make sure the state has changed from the initial state
        const initialStateSelected = this.initialSelectionState[authorizedWidget];
        const finalStateSelected = !closedWidgets.includes(authorizedWidget);
        if (initialStateSelected !== finalStateSelected) {
          this.analyticsService.trackEvent({
            eventCategory: 'dashboard.widgets',
            eventAction: finalStateSelected ? 'add' : 'remove',
            eventLabel: authorizedWidget,
          });
        }
      })
    );
  }

  onSelectionChange() {
    this.canSave = true;
  }
}
